import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/stores/auth';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    sun: <svg {...p}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>,
    users: <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>,
    target: <svg {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '14px 16px', background: 'var(--soleil-cream)',
  border: '1.5px solid var(--soleil-sand)', borderRadius: 11,
  color: 'var(--soleil-ink)', fontFamily: S.font, fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

export default function SoleilLoginPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password, rememberMe);
      navigate('/dashboard');
    } catch (err: any) {
      if (err.message?.includes('verify your email')) {
        navigate(`/check-email?email=${encodeURIComponent(email)}`);
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '86vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--soleil-cream)', padding: 40 }}>
      <div className="soleil-login-wrapper" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', maxWidth: 880, width: '100%', borderRadius: 22, overflow: 'hidden', boxShadow: '0 12px 48px rgba(46,42,38,0.1)' }}>
        {/* Left — dark panel */}
        <div className="soleil-login-panel" style={{
          background: 'linear-gradient(160deg, var(--soleil-dark), var(--soleil-d2))',
          padding: '56px 44px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', bottom: '-15%', right: '-10%', width: '50%', height: '50%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(184,92,58,0.1), transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 44 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ic type="sun" size={14} color="var(--soleil-dark)" />
              </div>
              <span style={{ fontFamily: S.heading, fontSize: 16, fontWeight: 600, color: 'var(--soleil-cream)' }}>ITIL Shift</span>
            </div>
            <h2 style={{ fontFamily: S.heading, fontSize: 36, fontWeight: 600, color: 'var(--soleil-cream)', lineHeight: 1.15 }}>
              {isRTL ? 'ترقيتك المهنية ' : 'Your career upgrade '}
              <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
                {isRTL ? 'تبدأ هنا' : 'starts here'}
              </span>.
            </h2>
            <p style={{ fontFamily: S.font, fontSize: 14, color: 'rgba(250,248,244,0.48)', lineHeight: 1.7, marginTop: 16 }}>
              {isRTL ? 'انضم إلى منصة ITIL الرائدة في العراق' : "Join Iraq's leading ITIL platform."}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 28, paddingTop: 24, borderTop: '1px solid rgba(250,248,244,0.08)', position: 'relative', zIndex: 2 }}>
            {[['500+', isRTL ? 'طالب' : 'Students', 'users'], ['98%', isRTL ? 'نسبة النجاح' : 'Pass Rate', 'target']].map(([v, l, ic], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Ic type={ic} size={16} color="var(--soleil-sienna)" />
                <div>
                  <div style={{ fontFamily: S.heading, fontSize: 20, fontWeight: 600, color: 'var(--soleil-gold)' }}>{v}</div>
                  <div style={{ fontFamily: S.font, fontSize: 10, color: 'rgba(250,248,244,0.48)' }}>{l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <div style={{ padding: '56px 44px', background: '#fff' }}>
          <h2 style={{ fontFamily: S.heading, fontSize: 30, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 6 }}>
            {t('auth.login.title')}
          </h2>
          <p style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)', marginBottom: 32 }}>
            {isRTL ? 'سجل دخولك لمتابعة التعلم' : 'Sign in to continue learning'}
          </p>

          {error && (
            <div style={{ background: 'var(--soleil-sienna-pale)', color: 'var(--soleil-sienna)', padding: '12px 16px', borderRadius: 10, fontFamily: S.font, fontSize: 13, marginBottom: 18, border: '1px solid rgba(184,92,58,0.2)' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sub)', display: 'block', marginBottom: 7 }}>
                {t('auth.login.email')}
              </label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder={t('auth.login.email')} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sub)', display: 'block', marginBottom: 7 }}>
                {t('auth.login.password')}
              </label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" placeholder={t('auth.login.password')} style={inputStyle} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <label style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-sub)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                {t('auth.login.remember')}
              </label>
              <Link to="/forgot-password" style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: 'var(--soleil-sienna)', textDecoration: 'none' }}>
                {t('auth.login.forgot')}
              </Link>
            </div>

            <button type="submit" disabled={isLoading} style={{
              width: '100%', padding: 15, fontFamily: S.font, fontSize: 15, fontWeight: 600,
              color: 'var(--soleil-dark)',
              background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
              border: 'none', borderRadius: 11, cursor: isLoading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 16px rgba(184,148,58,0.2)', opacity: isLoading ? 0.7 : 1,
            }}>
              {isLoading ? (t('common.loading') || 'Loading...') : t('auth.login.submit')}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 22, fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)' }}>
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" style={{ color: 'var(--soleil-sienna)', fontWeight: 600, textDecoration: 'none' }}>
              {t('auth.login.register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
